import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

/**
 * Custom hook for managing transaction data, pagination, and filtering.
 */
export const useTransactions = (activeProject, paginationEnabled = true, successCallback = null) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingTxs, setUpdatingTxs] = useState({}); // { [txId]: boolean }
  const [error, setError] = useState(null);
  const [beginningBalance, setBeginningBalance] = useState(0);
  const [endingBalance, setEndingBalance] = useState(0);
  const requestCountRef = useRef(0);
  
  // Pagination & Filter State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [payeeFilter, setPayeeFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [onlyUncategorized, setOnlyUncategorized] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [accountIdFilter, setAccountIdFilter] = useState('');
  const [postingFilter, setPostingFilter] = useState('pending');

  // Restore persisted working account filter on project change
  useEffect(() => {
    if (activeProject?._id) {
      const persisted = localStorage.getItem(`working_account_filter_${activeProject._id}`) || '';
      setAccountIdFilter(persisted);
    } else {
      setAccountIdFilter('');
    }
  }, [activeProject?._id]);

  // Persist working account filter when it changes
  useEffect(() => {
    if (activeProject?._id) {
      if (accountIdFilter) {
        localStorage.setItem(`working_account_filter_${activeProject._id}`, accountIdFilter);
      } else {
        localStorage.removeItem(`working_account_filter_${activeProject._id}`);
      }
    }
  }, [accountIdFilter, activeProject?._id]);

  const fetchTransactions = useCallback(async (silent = false) => {
    if (!activeProject?._id) return;
    const currentRequest = ++requestCountRef.current;
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const query = new URLSearchParams({
        page,
        limit,
        sortBy,
        sortOrder,
        ...(paginationEnabled === false && { disablePagination: 'true' }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(accountIdFilter && { accountId: accountIdFilter }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(payeeFilter && { payee: payeeFilter }),
        ...(classFilter && { class: classFilter }),
        ...(descriptionFilter && { description: descriptionFilter }),
        ...(onlyUncategorized && { uncategorized: 'true' }),
        ...(postingFilter === 'posted' && { posting: 'true' }),
        ...(postingFilter === 'pending' && { posting: 'false' }),
        ...(postingFilter === 'excluded' && { excluded: 'true' })
      });
      const res = await axios.get(`/api/projects/${activeProject._id}/transactions?${query.toString()}`);
  // ...

      if (currentRequest !== requestCountRef.current) return;

      if (res.data && res.data.transactions) {
        setTransactions(res.data.transactions);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 0);
        setBeginningBalance(res.data.beginningBalance || 0);
        setEndingBalance(res.data.endingBalance || 0);
      } else if (Array.isArray(res.data)) {
        setTransactions(res.data);
        setTotal(res.data.length);
        setPages(1);
        setBeginningBalance(0);
        setEndingBalance(0);
      } else {
        setTransactions([]);
        setTotal(0);
        setPages(0);
        setBeginningBalance(0);
        setEndingBalance(0);
      }
    } catch (err) { 
      if (currentRequest !== requestCountRef.current) return;
      console.error('Fetch error:', err);
      const msg = err.response?.data?.error || err.response?.data?.msg || 'Failed to fetch transactions';
      setError(msg);
      setTransactions([]);
    } finally {
      if (currentRequest === requestCountRef.current) {
        setLoading(false);
      }
    }
  }, [activeProject?._id, page, limit, sortBy, sortOrder, startDate, endDate, accountIdFilter, categoryFilter, payeeFilter, classFilter, descriptionFilter, onlyUncategorized, postingFilter]);

  const updateTransaction = async (txId, updates, accounts) => {
    // Set updating status for this transaction
    setUpdatingTxs(prev => ({ ...prev, [txId]: true }));

    // 1. Optimistic UI update
    let oldTransactions = [...transactions];
    setTransactions(prev => {
      // If the list is filtered, and the update changes matching criteria, remove it from list
      // For instance, if filter is set to "Pending" (postingFilter === "pending") and we post it (posting = true),
      // we remove the item from the pending list instead of keeping it updated in place.
      const shouldFilterOut = 
        (postingFilter === 'pending' && updates.posting === true) ||
        (postingFilter === 'posted' && updates.posting === false) ||
        (postingFilter === 'excluded' && updates.excluded === false) ||
        (postingFilter !== 'excluded' && updates.excluded === true);

      if (shouldFilterOut) {
        return prev.filter(tx => tx._id !== txId);
      }

      return prev.map(tx => {
        if (tx._id === txId) {
          const updated = { ...tx, ...updates };
          // If account is updating, resolve the toAccount object locally
          if (updates.toAccountId !== undefined) {
            const targetAcc = accounts.find(a => a._id === updates.toAccountId);
            updated.toAccount = targetAcc ? { _id: targetAcc._id, name: targetAcc.name, type: targetAcc.type } : null;
            updated.category = targetAcc ? targetAcc.name : 'Uncategorized';
          }
          return updated;
        }
        return tx;
      });
    });

    // Client-side validation: prevent posting if uncategorized
    if (updates.posting === true) {
      const currentTx = oldTransactions.find(t => t._id === txId);
      const targetToAccount = updates.toAccountId !== undefined ? updates.toAccountId : currentTx?.toAccount;
      const targetFromAccount = currentTx?.fromAccount;
      const targetCategory = updates.toAccountId !== undefined 
        ? (accounts.find(a => a._id === updates.toAccountId)?.name || 'Uncategorized')
        : currentTx?.category;

      if (!targetFromAccount || !targetToAccount || targetCategory === 'Uncategorized') {
        alert('Cannot post uncategorized transaction. Please assign a Category/Transfer account first.');
        setUpdatingTxs(prev => ({ ...prev, [txId]: false }));
        return false;
      }
    }

    try {
      const { toAccountId, payeeId, classId, note, date, clearedStatus, posting, excluded } = updates;
      let category = undefined;
      if (toAccountId !== undefined) {
        const targetAcc = accounts.find(a => a._id === toAccountId);
        category = targetAcc ? targetAcc.name : 'Uncategorized';
      }
      
      await axios.put(`/api/projects/${activeProject._id}/transactions/${txId}`, { 
        toAccountId,
        payeeId,
        classId,
        category,
        note,
        date,
        clearedStatus,
        posting,
        excluded
      });
      setUpdatingTxs(prev => ({ ...prev, [txId]: false }));
      if (successCallback) {
        successCallback();
      }
      return true;
    } catch (err) { 
      console.error('Update error:', err);
      // Rollback on failure
      setTransactions(oldTransactions);
      setUpdatingTxs(prev => ({ ...prev, [txId]: false }));
      return false;
    }
  };

  const deleteTransaction = async (txOrId) => {
    const isObject = typeof txOrId === 'object' && txOrId !== null;
    const txId = isObject ? txOrId._id : txOrId;
    const isImported = isObject && (txOrId.type === 'Imported' || txOrId.plaidTransactionId || txOrId.documentId);
    
    let confirmMessage = 'Are you sure you want to permanently delete this transaction?';
    if (isImported) {
      if (txOrId.posting) {
        confirmMessage = 'Are you sure you want to unpost this transaction and move it back to Pending?';
      } else if (txOrId.excluded) {
        confirmMessage = 'Are you sure you want to permanently delete this excluded transaction from the database?';
      } else {
        confirmMessage = 'Are you sure you want to exclude this transaction and move it to Excluded?';
      }
    }

    if (!window.confirm(confirmMessage)) return;
    try {
      await axios.delete(`/api/projects/${activeProject._id}/transactions/${txId}`);
      fetchTransactions();
      return true;
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error processing request: ' + (err.response?.data?.msg || ''));
      return false;
    }
  };

  const bulkCategorize = async (transactionIds, toAccountId, category, payeeId, classId, posting, excluded) => {
    try {
      await axios.post(`/api/projects/${activeProject._id}/transactions/bulk-update`, {
        transactionIds,
        toAccountId,
        category,
        payeeId,
        classId,
        posting,
        excluded
      });
      fetchTransactions(true);
      return true;
    } catch (err) {
      console.error('Bulk categorize error:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  return {
    transactions,
    loading,
    updatingTxs,
    error,
    page, setPage,
    limit, setLimit,
    total,
    pages,
    startDate, setStartDate,
    endDate, setEndDate,
    accountIdFilter, setAccountIdFilter,
    categoryFilter, setCategoryFilter,
    payeeFilter, setPayeeFilter,
    classFilter, setClassFilter,
    descriptionFilter, setDescriptionFilter,
    onlyUncategorized, setOnlyUncategorized,
    postingFilter, setPostingFilter,
    sortBy,
    sortOrder,
    handleSort,
    fetchTransactions,
    updateTransaction,
    deleteTransaction,
    bulkCategorize,
    beginningBalance,
    endingBalance
  };
};
